// AI-Generated Code - 2025-02-22 - Privacy Validation page (new features UI)

import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Shield,
  AlertTriangle,
  CheckCircle,
  FileCheck,
  GitCompare,
  ListChecks,
  Webhook,
  Plus,
  Trash2,
  RefreshCw,
} from "lucide-react";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";

interface PrivacyDashboardSummary {
  totalOpenFindings: number;
  totalFixed: number;
  totalVerified: number;
  byUrl: { url: string; openFindings: number; lastScan: string | null }[];
}

interface PrivacyFinding {
  id: string;
  url: string;
  type: string;
  severity: string;
  title: string;
  description?: string;
  status: "open" | "fixed" | "verified";
  createdAt: string;
}

interface DriftSummary {
  scanId: number;
  newScriptsCount: number;
  removedScriptsCount: number;
  modifiedScriptsCount: number;
  newEndpointsCount: number;
  removedEndpointsCount: number;
  overallSeverity?: string;
  pciRelevant?: boolean;
  scriptChanges?: { src: string; changeType: string }[];
  endpointChanges?: { method: string; url: string; changeType: string }[];
}

export default function PrivacyValidation() {
  const [findingsStatusFilter, setFindingsStatusFilter] = useState<string>("open");
  const [driftUrl, setDriftUrl] = useState("");
  const [policyUrlOrDomain, setPolicyUrlOrDomain] = useState("");
  const [newScriptPattern, setNewScriptPattern] = useState("");
  const [newVendorDomain, setNewVendorDomain] = useState("");
  const [newVendorName, setNewVendorName] = useState("");
  const [webhookUrl, setWebhookUrl] = useState("");
  const [webhookEvents, setWebhookEvents] = useState("privacy_violation,drift_detected,remediation_verified");
  const [tagResourceType, setTagResourceType] = useState<"script" | "vendor">("vendor");
  const [tagResourceId, setTagResourceId] = useState("");
  const [tagName, setTagName] = useState("");
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: dashboard, isLoading: dashboardLoading, error: dashboardError } = useQuery<PrivacyDashboardSummary>({
    queryKey: ["/api/privacy-dashboard"],
    queryFn: () => apiRequest("/api/privacy-dashboard"),
  });

  const { data: findingsData, isLoading: findingsLoading } = useQuery<{ privacyFindings: PrivacyFinding[] }>({
    queryKey: ["/api/privacy-findings", findingsStatusFilter],
    queryFn: () => apiRequest(`/api/privacy-findings${findingsStatusFilter ? `?status=${encodeURIComponent(findingsStatusFilter)}` : ""}`),
  });
  const findings = findingsData?.privacyFindings ?? [];

  const { data: driftData, isLoading: driftLoading, refetch: refetchDrift } = useQuery<{ drift: DriftSummary | null }>({
    queryKey: ["/api/drift", driftUrl],
    queryFn: () => apiRequest(`/api/drift?url=${encodeURIComponent(driftUrl)}`),
    enabled: !!driftUrl.trim(),
  });

  const domainFromPolicyUrl = policyUrlOrDomain.trim() ? (() => { try { return new URL(policyUrlOrDomain.trim().startsWith("http") ? policyUrlOrDomain : `https://${policyUrlOrDomain}/`).hostname; } catch { return policyUrlOrDomain.trim(); } })() : "";
  const comparisonUrl = policyUrlOrDomain.trim() ? (policyUrlOrDomain.trim().startsWith("http") ? policyUrlOrDomain.trim() : `https://${policyUrlOrDomain.trim()}/`) : "";
  const { data: policyData, isLoading: policyLoading, refetch: refetchPolicy } = useQuery({
    queryKey: ["/api/policy-comparison", comparisonUrl],
    queryFn: () => apiRequest(`/api/policy-comparison?url=${encodeURIComponent(comparisonUrl)}`),
    enabled: !!domainFromPolicyUrl && !!comparisonUrl,
  });

  const { data: declaredData } = useQuery<{ declaredScripts: { id: string; domain: string; scriptUrlOrPattern: string }[] }>({
    queryKey: ["/api/declared-scripts", domainFromPolicyUrl],
    queryFn: () => apiRequest(`/api/declared-scripts?domain=${encodeURIComponent(domainFromPolicyUrl)}`),
    enabled: !!domainFromPolicyUrl,
  });
  const declaredScripts = declaredData?.declaredScripts ?? [];

  const { data: vendorsData } = useQuery<{ authorizedVendors: { id: string; domain: string; vendorDomain: string; vendorName?: string }[] }>({
    queryKey: ["/api/authorized-vendors", domainFromPolicyUrl],
    queryFn: () => apiRequest(`/api/authorized-vendors?domain=${encodeURIComponent(domainFromPolicyUrl)}`),
    enabled: !!domainFromPolicyUrl,
  });
  const authorizedVendors = vendorsData?.authorizedVendors ?? [];

  const { data: webhooksData } = useQuery<{ privacyWebhooks: { id: string; url: string; events: string }[] }>({
    queryKey: ["/api/privacy-webhooks"],
  });
  const webhooks = webhooksData?.privacyWebhooks ?? [];

  const { data: tagsData } = useQuery<{ resourceTags: { id: string; resourceType: string; resourceId: string; tag: string }[] }>({
    queryKey: ["/api/resource-tags"],
    queryFn: () => apiRequest("/api/resource-tags"),
  });
  const resourceTags = tagsData?.resourceTags ?? [];

  const updateFindingStatus = useMutation({
    mutationFn: async ({ id, status }: { id: string; status: string }) =>
      apiRequest(`/api/privacy-findings/${id}`, { method: "PATCH", body: { status } }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/privacy-findings"] });
      queryClient.invalidateQueries({ queryKey: ["/api/privacy-dashboard"] });
      toast({ title: "Status updated" });
    },
    onError: (e: Error) => toast({ title: "Update failed", description: e.message, variant: "destructive" }),
  });

  const verifyFinding = useMutation({
    mutationFn: async (id: string) => apiRequest(`/api/privacy-findings/${id}/verify`, { method: "POST" }),
    onSuccess: (data: { verified?: boolean }) => {
      queryClient.invalidateQueries({ queryKey: ["/api/privacy-findings"] });
      queryClient.invalidateQueries({ queryKey: ["/api/privacy-dashboard"] });
      toast({ title: data?.verified ? "Finding verified" : "Still has drift" });
    },
    onError: (e: Error) => toast({ title: "Verify failed", description: e.message, variant: "destructive" }),
  });

  const addDeclaredScript = useMutation({
    mutationFn: async () => {
      if (!domainFromPolicyUrl || !newScriptPattern.trim()) throw new Error("Domain and script pattern required");
      return apiRequest("/api/declared-scripts", { method: "POST", body: { domain: domainFromPolicyUrl, scriptUrlOrPattern: newScriptPattern.trim() } });
    },
    onSuccess: () => {
      setNewScriptPattern("");
      queryClient.invalidateQueries({ queryKey: ["/api/declared-scripts"] });
      queryClient.invalidateQueries({ queryKey: ["/api/policy-comparison"] });
      toast({ title: "Declared script added" });
    },
    onError: (e: Error) => toast({ title: "Add failed", description: e.message, variant: "destructive" }),
  });

  const deleteDeclaredScript = useMutation({
    mutationFn: (id: string) => apiRequest(`/api/declared-scripts/${id}`, { method: "DELETE" }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/declared-scripts"] });
      queryClient.invalidateQueries({ queryKey: ["/api/policy-comparison"] });
      toast({ title: "Script removed" });
    },
  });

  const addVendor = useMutation({
    mutationFn: async () => {
      if (!domainFromPolicyUrl || !newVendorDomain.trim()) throw new Error("Domain and vendor domain required");
      return apiRequest("/api/authorized-vendors", { method: "POST", body: { domain: domainFromPolicyUrl, vendorDomain: newVendorDomain.trim(), vendorName: newVendorName.trim() || undefined } });
    },
    onSuccess: () => {
      setNewVendorDomain("");
      setNewVendorName("");
      queryClient.invalidateQueries({ queryKey: ["/api/authorized-vendors"] });
      queryClient.invalidateQueries({ queryKey: ["/api/policy-comparison"] });
      toast({ title: "Vendor added" });
    },
    onError: (e: Error) => toast({ title: "Add failed", description: e.message, variant: "destructive" }),
  });

  const deleteVendor = useMutation({
    mutationFn: (id: string) => apiRequest(`/api/authorized-vendors/${id}`, { method: "DELETE" }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/authorized-vendors"] });
      queryClient.invalidateQueries({ queryKey: ["/api/policy-comparison"] });
      toast({ title: "Vendor removed" });
    },
  });

  const initializeFromScan = useMutation({
    mutationFn: () => apiRequest("/api/policy/initialize-from-scan", { method: "POST", body: { url: comparisonUrl } }),
    onSuccess: (data: { declaredScriptsAdded?: number; authorizedVendorsAdded?: number; message?: string }) => {
      queryClient.invalidateQueries({ queryKey: ["/api/declared-scripts"] });
      queryClient.invalidateQueries({ queryKey: ["/api/authorized-vendors"] });
      queryClient.invalidateQueries({ queryKey: ["/api/policy-comparison"] });
      toast({
        title: "Policy initialized from scan",
        description: `Added ${data?.declaredScriptsAdded ?? 0} scripts and ${data?.authorizedVendorsAdded ?? 0} vendors for review. Remove any you don’t want; what remains is your baseline.`,
      });
    },
    onError: (e: Error) => toast({ title: "Initialize failed", description: e.message, variant: "destructive" }),
  });

  const addWebhook = useMutation({
    mutationFn: async () => {
      if (!webhookUrl.trim()) throw new Error("Webhook URL required");
      return apiRequest("/api/privacy-webhooks", { method: "POST", body: { url: webhookUrl.trim(), events: webhookEvents } });
    },
    onSuccess: () => {
      setWebhookUrl("");
      queryClient.invalidateQueries({ queryKey: ["/api/privacy-webhooks"] });
      toast({ title: "Webhook added" });
    },
    onError: (e: Error) => toast({ title: "Add failed", description: e.message, variant: "destructive" }),
  });

  const addTag = useMutation({
    mutationFn: async () => {
      if (!tagResourceId.trim() || !tagName.trim()) throw new Error("Resource ID and tag required");
      return apiRequest("/api/resource-tags", { method: "POST", body: { resourceType: tagResourceType, resourceId: tagResourceId.trim(), tag: tagName.trim() } });
    },
    onSuccess: () => {
      setTagResourceId("");
      setTagName("");
      queryClient.invalidateQueries({ queryKey: ["/api/resource-tags"] });
      toast({ title: "Tag added" });
    },
    onError: (e: Error) => toast({ title: "Add tag failed", description: e.message, variant: "destructive" }),
  });
  const deleteTag = useMutation({
    mutationFn: (id: string) => apiRequest(`/api/resource-tags/${id}`, { method: "DELETE" }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/resource-tags"] });
      toast({ title: "Tag removed" });
    },
  });
  const deleteWebhook = useMutation({
    mutationFn: (id: string) => apiRequest(`/api/privacy-webhooks/${id}`, { method: "DELETE" }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/privacy-webhooks"] });
      toast({ title: "Webhook removed" });
    },
  });

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6">
      <div>
        <h1 className="text-2xl font-semibold flex items-center gap-2">
          <Shield className="h-6 w-6" />
          Privacy Validation
        </h1>
        <p className="text-muted-foreground mt-1">
          Data flow classification, drift detection, policy comparison, and remediation tracking.
        </p>
      </div>

      <Tabs defaultValue="overview" className="w-full">
        <TabsList className="grid w-full grid-cols-4 lg:grid-cols-5">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="findings">Findings</TabsTrigger>
          <TabsTrigger value="drift">Drift</TabsTrigger>
          <TabsTrigger value="policy">Policy</TabsTrigger>
          <TabsTrigger value="tags">Tags</TabsTrigger>
          <TabsTrigger value="webhooks">Webhooks</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-4 mt-4">
          {dashboardError ? (
            <Card>
              <CardContent className="pt-6">
                <p className="text-destructive font-medium">Could not load privacy dashboard.</p>
                <p className="text-muted-foreground text-sm mt-1">Check the browser console or try refreshing. Ensure you are signed in.</p>
              </CardContent>
            </Card>
          ) : dashboardLoading ? (
            <p className="text-muted-foreground">Loading...</p>
          ) : (
            <>
              <div className="grid gap-4 md:grid-cols-3">
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium flex items-center gap-2">
                      <AlertTriangle className="h-4 w-4 text-amber-500" /> Open findings
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-2xl font-bold">{dashboard?.totalOpenFindings ?? 0}</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium flex items-center gap-2">
                      <FileCheck className="h-4 w-4" /> Fixed
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-2xl font-bold">{dashboard?.totalFixed ?? 0}</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-emerald-500" /> Verified
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-2xl font-bold">{dashboard?.totalVerified ?? 0}</p>
                  </CardContent>
                </Card>
              </div>
              <Card>
                <CardHeader>
                  <CardTitle>By URL</CardTitle>
                  <CardDescription>Last scan and open findings per URL (from your completed scans)</CardDescription>
                </CardHeader>
                <CardContent>
                  {!(Array.isArray(dashboard?.byUrl) && dashboard.byUrl.length > 0) ? (
                    <div className="text-muted-foreground text-sm space-y-2">
                      <p>No URLs with completed scans yet.</p>
                      <p>Run a scan from the main Dashboard (Site Data Overview), wait for it to complete, then return here. This section will list each scanned URL and its last scan time. Privacy findings (open/fixed/verified) and violation reports appear after you run the migration and run another scan.</p>
                      <p className="text-xs mt-2">To enable violation reports: run <code className="bg-muted px-1 rounded">psql $DATABASE_URL -f server/migrations/001_privacy_validation_features.sql</code> then run a new scan.</p>
                    </div>
                  ) : (
                    <ul className="space-y-2">
                      {(dashboard?.byUrl ?? []).map(({ url, openFindings, lastScan }) => (
                        <li key={url} className="flex items-center justify-between py-2 border-b last:border-0">
                          <span className="text-sm truncate max-w-md" title={url}>{url}</span>
                          <div className="flex items-center gap-3">
                            {openFindings > 0 && <Badge variant="destructive">{openFindings} open</Badge>}
                            {lastScan && <span className="text-xs text-muted-foreground">{new Date(lastScan).toLocaleDateString()}</span>}
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                </CardContent>
              </Card>
            </>
          )}
        </TabsContent>

        <TabsContent value="findings" className="space-y-4 mt-4">
          <div className="flex gap-2 items-center">
            <span className="text-sm text-muted-foreground">Status:</span>
            {["open", "fixed", "verified"].map((s) => (
              <Button key={s} variant={findingsStatusFilter === s ? "default" : "outline"} size="sm" onClick={() => setFindingsStatusFilter(s)}>
                {s}
              </Button>
            ))}
          </div>
          {findingsLoading ? (
            <p className="text-muted-foreground">Loading...</p>
          ) : !findings.length ? (
            <div className="text-muted-foreground text-sm space-y-1">
              <p>No findings for this filter.</p>
              <p>Privacy violation findings are created after scans when drift or policy mismatches are detected. Run the migration (see Overview tab) and run a new scan to generate reports.</p>
            </div>
          ) : (
            <ul className="space-y-3">
              {findings.map((f) => (
                <Card key={f.id}>
                  <CardContent className="pt-4">
                    <div className="flex flex-wrap items-start justify-between gap-2">
                      <div>
                        <p className="font-medium">{f.title}</p>
                        <p className="text-sm text-muted-foreground truncate max-w-xl" title={f.url}>{f.url}</p>
                        <div className="flex gap-2 mt-2">
                          <Badge variant="outline">{f.type}</Badge>
                          <Badge variant="outline">{f.severity}</Badge>
                          <Badge>{f.status}</Badge>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        {f.status === "open" && (
                          <>
                            <Button size="sm" variant="outline" onClick={() => updateFindingStatus.mutate({ id: f.id, status: "fixed" })}>
                              Mark fixed
                            </Button>
                            <Button size="sm" onClick={() => verifyFinding.mutate(f.id)} disabled={verifyFinding.isPending}>
                              Verify
                            </Button>
                          </>
                        )}
                        {f.status === "fixed" && (
                          <Button size="sm" onClick={() => verifyFinding.mutate(f.id)} disabled={verifyFinding.isPending}>
                            Verify remediation
                          </Button>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </ul>
          )}
        </TabsContent>

        <TabsContent value="drift" className="space-y-4 mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2"><GitCompare className="h-5 w-5" /> Baseline drift</CardTitle>
              <CardDescription>Compare latest scan to the previous one for a URL. Requires two completed scans.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex gap-2">
                <Input
                  placeholder="https://example.com"
                  value={driftUrl}
                  onChange={(e) => setDriftUrl(e.target.value)}
                  className="max-w-md"
                />
                <Button onClick={() => refetchDrift()} disabled={!driftUrl.trim() || driftLoading}>
                  <RefreshCw className={`h-4 w-4 mr-1 ${driftLoading ? "animate-spin" : ""}`} /> Check drift
                </Button>
              </div>
              {driftData !== undefined && (
                driftData.drift == null ? (
                  <p className="text-muted-foreground text-sm">No drift data (need at least two completed scans for this URL).</p>
                ) : (
                  <div className="rounded-lg border p-4 space-y-2 text-sm">
                    <p><strong>Scan ID:</strong> {driftData.drift.scanId}</p>
                    <p><strong>Scripts:</strong> +{driftData.drift.newScriptsCount} new, −{driftData.drift.removedScriptsCount} removed, ~{driftData.drift.modifiedScriptsCount} modified</p>
                    <p><strong>Endpoints:</strong> +{driftData.drift.newEndpointsCount} new, −{driftData.drift.removedEndpointsCount} removed</p>
                    {driftData.drift.overallSeverity && <p><strong>Severity:</strong> <Badge>{driftData.drift.overallSeverity}</Badge></p>}
                    {driftData.drift.pciRelevant && <Badge variant="secondary">PCI relevant</Badge>}
                  </div>
                )
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="policy" className="space-y-4 mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2"><ListChecks className="h-5 w-5" /> Policy vs actual</CardTitle>
              <CardDescription>Enter a page URL or domain to manage declared scripts and authorized vendors, and run policy comparison. Use &quot;Initialize from latest scan&quot; to add all current scripts and vendors from your last scan for review; remove any you don’t want—what remains is your baseline.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex gap-2 flex-wrap items-center">
                <Input placeholder="https://example.com or example.com" value={policyUrlOrDomain} onChange={(e) => setPolicyUrlOrDomain(e.target.value)} className="max-w-sm" />
                <Button variant="outline" size="sm" onClick={() => refetchPolicy()} disabled={!domainFromPolicyUrl}>Refresh comparison</Button>
                <Button size="sm" onClick={() => initializeFromScan.mutate()} disabled={!comparisonUrl || initializeFromScan.isPending}>
                  {initializeFromScan.isPending ? "Initializing…" : "Initialize from latest scan"}
                </Button>
              </div>
              {domainFromPolicyUrl && (
                <>
                  <div className="grid gap-6 md:grid-cols-2">
                    <div>
                      <h4 className="font-medium mb-2">Declared scripts</h4>
                      <div className="flex gap-2 mb-2">
                        <Input placeholder="Script URL or pattern (e.g. https://cdn.example.com/*)" value={newScriptPattern} onChange={(e) => setNewScriptPattern(e.target.value)} />
                        <Button size="sm" onClick={() => addDeclaredScript.mutate()} disabled={!newScriptPattern.trim() || addDeclaredScript.isPending}><Plus className="h-4 w-4" /></Button>
                      </div>
                      <ul className="space-y-1 text-sm">
                        {declaredScripts.map((s) => (
                          <li key={s.id} className="flex items-center justify-between gap-2">
                            <span className="truncate" title={s.scriptUrlOrPattern}>{s.scriptUrlOrPattern}</span>
                            <Button size="sm" variant="ghost" onClick={() => deleteDeclaredScript.mutate(s.id)}><Trash2 className="h-3 w-3" /></Button>
                          </li>
                        ))}
                        {!declaredScripts.length && <li className="text-muted-foreground">None</li>}
                      </ul>
                    </div>
                    <div>
                      <h4 className="font-medium mb-2">Authorized vendors</h4>
                      <div className="flex gap-2 mb-2 flex-wrap">
                        <Input placeholder="Vendor domain" value={newVendorDomain} onChange={(e) => setNewVendorDomain(e.target.value)} className="w-32" />
                        <Input placeholder="Name (optional)" value={newVendorName} onChange={(e) => setNewVendorName(e.target.value)} className="w-32" />
                        <Button size="sm" onClick={() => addVendor.mutate()} disabled={!newVendorDomain.trim() || addVendor.isPending}><Plus className="h-4 w-4" /></Button>
                      </div>
                      <ul className="space-y-1 text-sm">
                        {authorizedVendors.map((v) => (
                          <li key={v.id} className="flex items-center justify-between gap-2">
                            <span>{v.vendorDomain}{v.vendorName ? ` (${v.vendorName})` : ""}</span>
                            <Button size="sm" variant="ghost" onClick={() => deleteVendor.mutate(v.id)}><Trash2 className="h-3 w-3" /></Button>
                          </li>
                        ))}
                        {!authorizedVendors.length && <li className="text-muted-foreground">None</li>}
                      </ul>
                    </div>
                  </div>
                  {policyLoading ? <p className="text-sm text-muted-foreground">Loading comparison...</p> : policyData && (
                    <div className="rounded-lg border p-4 text-sm space-y-4">
                      <h4 className="font-medium mb-2">Comparison result</h4>
                      <p>Undeclared scripts: {(policyData as { undeclaredScripts?: unknown[] }).undeclaredScripts?.length ?? 0}</p>
                      <p>Unauthorized vendors: {(policyData as { unauthorizedVendors?: { domain: string; vendorName?: string | null; category?: string | null; riskTier?: string }[] }).unauthorizedVendors?.length ?? 0}</p>
                      {Array.isArray((policyData as { unauthorizedVendors?: { domain: string; vendorName?: string | null; category?: string | null; riskTier?: string }[] }).unauthorizedVendors) &&
                       (policyData as { unauthorizedVendors: { domain: string; vendorName?: string | null; category?: string | null; riskTier?: string }[] }).unauthorizedVendors.length > 0 && (
                        <div className="mt-2">
                          <h5 className="font-medium text-muted-foreground mb-1">Unauthorized vendors (with risk)</h5>
                          <ul className="space-y-1">
                            {(policyData as { unauthorizedVendors: { domain: string; vendorName?: string | null; category?: string | null; riskTier?: string }[] }).unauthorizedVendors.map((v: { domain: string; vendorName?: string | null; category?: string | null; riskTier?: string }) => (
                              <li key={v.domain} className="flex flex-wrap items-center gap-2">
                                <span>{v.domain}</span>
                                {v.vendorName && <Badge variant="secondary">{v.vendorName}</Badge>}
                                {v.category && <span className="text-muted-foreground">{v.category}</span>}
                                {v.riskTier && <Badge variant={v.riskTier === "high" || v.riskTier === "critical" ? "destructive" : "outline"}>{v.riskTier}</Badge>}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="tags" className="space-y-4 mt-4">
          <Card>
            <CardHeader>
              <CardTitle>Custom tags</CardTitle>
              <CardDescription>Tag scripts or vendors (e.g. &quot;marketing&quot;, &quot;review&quot;, &quot;approved&quot;) for filtering and policy review.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex gap-2 flex-wrap items-end">
                <div className="space-y-1">
                  <label className="text-sm font-medium">Type</label>
                  <select
                    className="h-9 rounded-md border border-input bg-background px-3 text-sm"
                    value={tagResourceType}
                    onChange={(e) => setTagResourceType(e.target.value as "script" | "vendor")}
                  >
                    <option value="script">Script</option>
                    <option value="vendor">Vendor</option>
                  </select>
                </div>
                <Input placeholder="Resource (script URL or vendor domain)" value={tagResourceId} onChange={(e) => setTagResourceId(e.target.value)} className="max-w-xs" />
                <Input placeholder="Tag name" value={tagName} onChange={(e) => setTagName(e.target.value)} className="w-28" />
                <Button size="sm" onClick={() => addTag.mutate()} disabled={!tagResourceId.trim() || !tagName.trim() || addTag.isPending}>Add tag</Button>
              </div>
              <ul className="space-y-2 text-sm">
                {resourceTags.map((t) => (
                  <li key={t.id} className="flex items-center justify-between py-1 border-b">
                    <span><Badge variant="outline">{t.resourceType}</Badge> {t.resourceId} → {t.tag}</span>
                    <Button size="sm" variant="ghost" onClick={() => deleteTag.mutate(t.id)}><Trash2 className="h-3 w-3" /></Button>
                  </li>
                ))}
                {!resourceTags.length && <li className="text-muted-foreground">No tags yet.</li>}
              </ul>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="webhooks" className="space-y-4 mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2"><Webhook className="h-5 w-5" /> SIEM / webhooks</CardTitle>
              <CardDescription>Receive privacy_violation, drift_detected, and remediation_verified events via POST.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex gap-2 flex-wrap">
                <Input placeholder="https://your-siem.example.com/webhook" value={webhookUrl} onChange={(e) => setWebhookUrl(e.target.value)} className="max-w-md" />
                <Input placeholder="privacy_violation,drift_detected,remediation_verified" value={webhookEvents} onChange={(e) => setWebhookEvents(e.target.value)} className="max-w-xs" />
                <Button onClick={() => addWebhook.mutate()} disabled={!webhookUrl.trim() || addWebhook.isPending}>Add webhook</Button>
              </div>
              <ul className="space-y-2">
                {webhooks.map((w) => (
                  <li key={w.id} className="flex items-center justify-between py-2 border-b">
                    <span className="text-sm truncate max-w-md" title={w.url}>{w.url}</span>
                    <span className="text-xs text-muted-foreground">{w.events}</span>
                    <Button size="sm" variant="ghost" onClick={() => deleteWebhook.mutate(w.id)}><Trash2 className="h-3 w-3" /></Button>
                  </li>
                ))}
                {!webhooks.length && <li className="text-muted-foreground text-sm">No webhooks configured.</li>}
              </ul>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
